import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { PageContent } from 'src/pages/page-content.js';
import '../../library/presentational/d-sections.js';
import '../d-page-header.js';
import '../../library/components/d-help-section.js';
import '../../library/elements/d-action.js';
import '../../library/elements/d-smooth-resize.js';
import './d-staffing-calendar.js';
import type { StaffingPageViewModel } from './staffing-page-view-model.js';
import { levelHeaderStyles } from 'src/library/level-header-styles.js';
import { features } from '../../store/selectors/features/features';
import { t } from 'src/i18n';

import type { Promo } from '../../store/selectors/promo-type';
import type { StaffingCalendar } from './staffing-calendar';
import type { StaffingEmployeeDayInput } from 'src/pages/staffing-page/staffing-employee-day-input';
import type { StaffingEmployeeDayResult } from 'src/pages/staffing-page/staffing-employee-day-result';
import { StaffingEmployeeDayDialog } from 'src/pages/staffing-page/staffing-employee-day-dialog';

/**
 *
 *
 * Migration:
 *  - Removed the spinner and 'pageReady' delay. Test first if performance is acceptable
 *  -
 *
 */
@customElement('d-staffing-page-content')
export class DStaffingPageContent extends PageContent<StaffingPageViewModel> {
  static readonly styles = [
    ...super.styles,
    levelHeaderStyles,
    css`
      :host {
        --page-width: 1000px;
        display: block;
        width: var(--page-width);
        min-height: calc(100vh + 1px);
        border-top-left-radius: 12px;
        background: var(--backgroundGray);
      }
      :host .content,
      :host(.width600) .content {
        padding: 0;
      }
      .no-horizontal-scroll > div > div {
        box-sizing: border-box;
        position: -webkit-sticky;
        position: sticky;
        max-width: 100vw;
        left: 0;
        background: var(--backgroundGray);
        transition: padding 0.3s;
      }
      :host(.width845) .header-wrapper > div > div {
        border-top-left-radius: 12px;
      }
      d-calendar-legend {
        margin-top: 12px;
      }
      d-staffing-calendar {
        margin-bottom: 20px;
      }
      d-sections {
        border-top: 1px solid var(--borderColorOnGray);
        border-bottom: 1px solid var(--borderColorOnGray);
      }
      .summaries > div {
        margin: 0 -8px 0 -8px;
      }
      .outside-range-info > div:first-child {
        padding-left: 38px;
        background: url(/images/info.svg) 3px 1px / 32px 32px no-repeat;
      }
      .outside-range-detail {
        padding: 2px 0 2px 38px;
        font-weight: 200;
      }
      .outside-range-detail .name {
        font-weight: normal;
      }
      .outside-range-link {
        margin: -4px -6px;
      }
      .timekeeping-before-employment-start-warning {
        padding: 6px 0 6px 38px;
        margin-bottom: 20px;
        background: url(/images/alert.svg) 1px 4px / 24px auto no-repeat;
      }
      .timekeeping-before-employment-start-warning .primary {
        margin-right: 6px;
        color: var(--alertColor);
        font-weight: 500;
      }
      .timekeeping-before-employment-start-warning .secondary {
        color: var(--alertColor);
        font-size: 14px;
        font-weight: 200;
      }
      .timekeeping-before-employment-start-warning a {
        color: var(--alertColor);
        font-weight: 400;
        text-decoration: none;
        border-bottom: 1px solid currentColor;
      }
      .timekeeping-before-employment-start-warning a:hover {
        color: black;
        border-bottom-color: var(--linkColorGray);
      }
    `,
  ];
  @property({ type: Number })
  appWidth = 1000;
  @property({ type: Number })
  calendarWidth = 1000;
  @property({ type: Boolean })
  uncoverPageMenu = false;
  @property({ type: Number })
  scrollLeft = 0;
  @property({ type: Object })
  scrollData = {};
  @property({ type: Object })
  employeeCalendarData!: StaffingCalendar;
  @property({ type: Boolean })
  showOutsideRangeDetails = false;

  private get horizontalPadding() {
    if (this.appWidth < 600) {
      return 20;
    }
    return 30;
  }

  private get stickyWrapperStyle() {
    return 'width: ' + this.calendarWidth + 'px;';
  }

  private get stickyStyle() {
    return (
      'width: ' +
      this.pageWidth +
      'px; padding-left: ' +
      this.horizontalPadding +
      'px; padding-right: ' +
      this.horizontalPadding +
      'px;'
    );
  }

  _onCalenderWidthChanged(e) {
    this.calendarWidth = e.detail;
    this.style.setProperty('--page-width', e.detail + 'px');
  }

  private async openEmployeeDayDialog(uuid: string, date: string, editWorkSchedules: boolean = false) {
    const employee = this.pageView.employeesForStaffingCalendar.find((employee) => employee.uuid === uuid);
    if (employee) {
      const tasks = employee.tasksLookup(date);
      const input: StaffingEmployeeDayInput = {
        date: date,
        employee: employee,
        currentEmployeeUuid: this.pageView.currentEmployeeUuid ?? '',
        currentUserHasAccess: this.pageView.currentUserHasExtendedStaffingAccess,
        leavePeriodEditRestriction: this.pageView.leavePeriodEditRestriction,
        writeAccess: this.writeAccess,
        tasks: tasks,
        workScheduleException: employee.workScheduleExceptions.find((exception) => exception.date === date),
        editWorkSchedules,
      };
      const result: StaffingEmployeeDayResult = await StaffingEmployeeDayDialog.open(input);
      console.log(result.action);
    }
  }

  private onEmployeeDayClick(e: CustomEvent<{ uuid: string; date: string }>) {
    e.stopPropagation();
    return this.openEmployeeDayDialog(e.detail.uuid, e.detail.date);
  }

  renderPromo(promoData: Promo) {
    return html` <d-promo-section
      theme-page
      .employeesCount=${this.pageView.employeesCount}
      .specialTerms=${this.pageView.specialTerms}
      .promoData=${promoData}
      .features=${features}
      .canUpgrade=${this.writeAccess}
    ></d-promo-section>`;
  }

  renderOutsideRangeSchedules(uuid: string, dates: { date: string; dateString: string }[]) {
    return html`
      <span>${t('staffing.outsideRangeWorkPatternLabel')} </span>
      ${dates.map((date) => {
        return html`
          <d-action plain class="outside-range-link" @click=${() => this.openEmployeeDayDialog(uuid, date.date, true)}
            >${date.dateString}</d-action
          >
        `;
      })}
    `;
  }

  renderOutsideRangeExceptions(uuid: string, dates: { date: string; dateString: string }[]) {
    return html`
      <span>${t('staffing.outsideRangeExceptionLabel')} </span>
      ${dates.map((date) => {
        return html`
          <d-action plain class="outside-range-link" @click=${() => this.openEmployeeDayDialog(uuid, date.date)}
            >${date.dateString}</d-action
          >
        `;
      })}
    `;
  }

  renderOutsideRangeDetails() {
    if (this.showOutsideRangeDetails) {
      return html`
        ${this.pageView.outsideRangeEmployees.map((e) => {
          return html`
            <div class="outside-range-detail">
              <span class="name">${e.name}</span>
              ${e.schedule
                ? html`${this.renderOutsideRangeSchedules(e.uuid, e.dates)}`
                : html`${this.renderOutsideRangeExceptions(e.uuid, e.dates)}`}
            </div>
          `;
        })}
      `;
    }
    return nothing;
  }

  renderOutsideRangeAlert() {
    if (this.pageView.outsideRangeEmployees.length > 0) {
      return html` <div class="outside-range-info">
        <div>
          ${t('staffing.outsideRangeInfo')}
          ${this.pageView.calendarDayRange.start}-${this.pageView.calendarDayRange.end}
          <d-action plain @click=${() => (this.showOutsideRangeDetails = !this.showOutsideRangeDetails)}>
            ${this.showOutsideRangeDetails ? t('staffing.hideDetails') : t('staffing.showDetails')}
          </d-action>
        </div>
        <d-smooth-resize> ${this.renderOutsideRangeDetails()} </d-smooth-resize>
      </div>`;
    }
    return nothing;
  }

  renderExtendedStaffingAccessSections() {
    if (this.pageView.currentUserHasExtendedStaffingAccess) {
      return html`
        <div class="summaries">
          <div>
            ${this.pageView.isMailinatorAccount
              ? html` <d-action href="${this.pageView.href}summaries/monthly-hours"
                  >${t('summaries.monthlyHoursTitle')}</d-action
                >`
              : nothing}
            <d-action plain href="${this.pageView.href}summaries/time-balance"
              >${t('summaries.timeBalanceTitle')}</d-action
            >
            <d-action plain href="${this.pageView.href}summaries/absence">${t('summaries.absenceTitle')}</d-action>
            <d-action plain href="${this.pageView.href}summaries/sick-leave">${t('summaries.sickLeaveTitle')}</d-action>
          </div>
        </div>
      `;
    }
    return nothing;
  }

  private renderTimekeepingBeforeEmploymentStartWarning() {
    const warning = this.pageView.timekeepingBeforeEmploymentStartWarning;
    const employees = warning.affectedEmployeeNames;
    if (employees.length === 0) {
      return nothing;
    }
    return html`
      <div class="timekeeping-before-employment-start-warning">
        <span class="primary">${warning.headerText}</span>
        <span class="secondary">
          ${warning.callToAction}. ${warning.employeeListIntro}:
          ${employees.map(
            (employee, index) =>
              html`${index > 0 ? ', ' : ''}
                <a href="${this.pageView.href}employees/${employee.uuid}?edit">${employee.name}</a>`,
          )}.
        </span>
      </div>
    `;
  }

  render() {
    return html`
      <div class="no-horizontal-scroll header-wrapper">
        <div style="${this.stickyWrapperStyle}">
          <div style="${this.stickyStyle}">${this.renderHeader()}</div>
        </div>
      </div>
      <div class="content" id="content">
        <div class="no-horizontal-scroll">
          <div style="${this.stickyWrapperStyle}">
            <div style="${this.stickyStyle}">
              ${this.pageView.promoData
                ? html`${this.renderPromo(this.pageView.promoData)} `
                : html`<d-help-section content="${this.pageView.helpContent}"></d-help-section>
                    <d-calendar-legend></d-calendar-legend> `}
            </div>
          </div>
        </div>
        ${!this.pageView.promoData
          ? html` <d-staffing-calendar
                .paddingLeft=${this.horizontalPadding}
                .appWidth=${this.appWidth}
                .pageWidth=${this.pageWidth}
                .contentStickyTop=${this.contentStickyTop}
                .scrollLeft=${this.scrollLeft}
                .year=${this.pageView.year}
                .showShareVacations=${false}
                .currentUserHasAccess=${this.pageView.currentUserHasExtendedStaffingAccess}
                .leavePeriodEditRestriction=${this.pageView.leavePeriodEditRestriction}
                .employeeCalendarData=${this.pageView.calendarData}
                .zoom=${this.pageView.zoom}
                .calendarDayRange=${this.pageView.calendarDayRange}
                .employees=${this.pageView.employeesForStaffingCalendar}
                .timekeepingBeforeEmploymentStartWarning=${this.pageView.timekeepingBeforeEmploymentStartWarning}
                .employeeGroups=${this.pageView.employeeGroups}
                .groupGraphData=${this.pageView.groupGraphData}
                .accessEmployees=${this.pageView.accessEmployees}
                .accessList=${this.pageView.accessList}
                .writeAccess=${this.pageView.writeAccess}
                .currentEmployeeUuid=${this.pageView.currentEmployeeUuid}
                @calendar-width-changed=${(e) => this._onCalenderWidthChanged(e)}
                @employee-day-click=${this.onEmployeeDayClick}
              >
              </d-staffing-calendar>
              <div class="no-horizontal-scroll">
                <div style="${this.stickyWrapperStyle}">
                  <div style="${this.stickyStyle}">
                    ${this.renderTimekeepingBeforeEmploymentStartWarning()}
                    <d-sections>
                      ${this.renderOutsideRangeAlert()}
                      <d-staffing-calendar-access
                        .currentUserHasAccess=${this.pageView.currentUserCanEditStaffingAccess}
                        .leavePeriodEditRestriction=${this.pageView.leavePeriodEditRestriction}
                        .employees=${this.pageView.accessEmployees}
                        .accessList=${this.pageView.accessList}
                      ></d-staffing-calendar-access>
                      <d-share-staffing
                        .currentUserHasAccess=${this.pageView.currentUserHasExtendedStaffingAccess}
                        .employeesOptions=${this.pageView.employeesForShare}
                        .employeesOrder=${this.pageView.employeesForStaffingCalendar.map((employee) => employee.uuid)}
                        .shares=${this.pageView.vacationSummaryShares}
                        .notes=${this.pageView.vacationSummaryNotes}
                        .selectedEmployees=${this.pageView.vacationSummaryEmployees}
                      ></d-share-staffing>
                      ${this.renderExtendedStaffingAccessSections()}
                    </d-sections>
                  </div>
                </div>
              </div>`
          : nothing}
      </div>
    `;
  }

  private scrollToPeriod() {
    // TODO
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-staffing-page-content': DStaffingPageContent;
  }
}
